import React, { useEffect, useState } from 'react';
import { Send, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '../../config/supabase';
import './ChatManagement.css';

const ChatManagement = () => {

    const [enquiries, setEnquiries] = useState([]);
    const [answeringId, setAnsweringId] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchEnquiries = async () => {
        try {
            setLoading(true);
            setError("");
            
            const { data, error: fetchError } = await supabase
                .from('community_questions')
                .select('*')
                .order('created_at', { ascending: false });

            if (fetchError) {
                console.error('Supabase error:', fetchError);
                throw fetchError;
            }

            console.log('Fetched enquiries:', data);

            const formatted = (data || []).map(q => ({
                id: q.id,
                user: q.user_name || "Anonymous",
                question: q.question,
                date: q.created_at?.split("T")[0],
                status: q.status === 'answered' ? "Answered" : "Pending",
                answer: q.answer || ""
            }));

            setEnquiries(formatted);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error('Fetch error:', err);
            setError("Unable to fetch enquiries: " + (err.message || 'Unknown error'));
            setEnquiries([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiries();

        // Real-time subscription
        const channel = supabase
            .channel('community_questions-changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'community_questions' }, (payload) => {
                console.log('Real-time update:', payload);
                fetchEnquiries();
            })
            .subscribe((status) => {
                console.log('Subscription status:', status);
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    const handleReplyClick = (id) => {
        setAnsweringId(id);
        setReplyText("");
    };

    const handleSubmitReply = async (id) => {
        if (!replyText.trim()) return;

        try {
            const { error: updateError } = await supabase
                .from('community_questions')
                .update({ 
                    answer: replyText, 
                    status: 'answered',
                    answered_at: new Date().toISOString()
                })
                .eq('id', id);

            if (updateError) throw updateError;

            setEnquiries(prev =>
                prev.map(enq =>
                    enq.id === id
                        ? { ...enq, status: "Answered", answer: replyText }
                        : enq
                )
            );

            setAnsweringId(null);
            setReplyText("");
        } catch (err) {
            console.error(err);
            alert("Answer submission failed");
        }
    };

    if (loading && enquiries.length === 0) return (
        <div className="admin-chat-container">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '40px' }}>
                <RefreshCw size={20} className="spin" />
                <span>Loading enquiries...</span>
            </div>
        </div>
    );
    
    if (error && enquiries.length === 0) return (
        <div className="admin-chat-container">
            <div style={{ padding: '20px', background: '#fff3cd', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ color: '#856404', margin: 0 }}>Error: {error}</p>
                <button onClick={fetchEnquiries} style={{ marginTop: '10px', padding: '8px 16px', background: '#1a237e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Retry
                </button>
            </div>
        </div>
    );

    return (
        <div className="admin-chat-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Public Chat Enquiries</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {lastUpdated && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                            Last updated: {lastUpdated}
                        </span>
                    )}
                    <button 
                        onClick={fetchEnquiries} 
                        disabled={loading}
                        style={{ padding: '6px 14px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>
            </div>

            <div className="chat-card">
                <table className="chat-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>User</th>
                            <th>Question</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {enquiries.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="no-enquiries">
                                    <CheckCircle size={40} />
                                    <div>No pending enquiries</div>
                                </td>
                            </tr>
                        ) : enquiries.map(enq => (
                            <tr key={enq.id}>
                                <td>#{enq.id}</td>
                                <td>
                                    <strong>{enq.user}</strong><br />
                                    <small>{enq.date}</small>
                                </td>
                                <td>{enq.question}</td>
                                <td>
                                    <span className={`status-badge ${
                                        enq.status === "Answered"
                                            ? "status-answered"
                                            : "status-pending"
                                    }`}>
                                        {enq.status === "Answered"
                                            ? <CheckCircle size={12} />
                                            : <Clock size={12} />}
                                        {enq.status}
                                    </span>
                                </td>
                                <td>
                                    {answeringId === enq.id ? (
                                        <>
                                            <textarea
                                                rows="3"
                                                className="reply-textarea"
                                                value={replyText}
                                                onChange={e => setReplyText(e.target.value)}
                                            />
                                            <button
                                                className="send-btn"
                                                onClick={() => handleSubmitReply(enq.id)}
                                            >
                                                <Send size={14} /> Send
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="reply-btn"
                                            onClick={() => handleReplyClick(enq.id)}
                                        >
                                            <Send size={14} /> Reply
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ChatManagement;
